// THE WORKSPACE BUILDS ITSELF — the head of the first workspace thread on the
// app builder (functions/workspaceSetup.js; lane WS1, Nika's decisions 9 and
// 11). It stands above the thread's messages (pages/add-app.js Chat.Messages):
//
//   the plan card   the plan note, in the shell's language
//                   (components/WorkspaceSetupPlan.js)
//   the setup       one line per step as it lands — the apps in install
//                   order, each in the navigation once done — the reason of a
//                   failed line in words and one Try again (owner / admin),
//                   the website card, the credits, the first build, and one
//                   way back to the site the person came from
//
// The summary note is the thread's first message under it (plain text, the
// shared chat's own row), then the person's first build.
//
// It holds no logic: every value comes from workspaceSetupView /
// workspaceSetupPlanView through `el.call`. Hidden on any other thread.
// Kept mounted and only hidden (Rule 66): the block appears once, when the
// thread opens, and its lines grow in place.
export const WorkspaceSetupThread = {
  tag: 'section',
  dataSetupThread: true,
  flow: 'y',
  gap: 'A',
  minWidth: '0',
  marginBottom: 'A',
  hideDeps: [
    (el, s) => s.root && s.root._workspaceSetup,
    (el, s) => s.root && s.root._ax_activeSessionId,
    (el, s) => s.root && s.root._ax_chatMessages
  ],
  hide: (el, s) =>
    !(el.call('workspaceSetupView', s.root) || {}).show &&
    !el.call('workspaceSetupPlanOf', s.root && s.root._ax_chatMessages),

  WorkspaceSetupPlanSlot: { planOf: 'thread' },

  SetupBlock: {
    role: 'status',
    ariaLive: 'polite',
    dataSetupStatus: (el, s) => (el.call('workspaceSetupView', s.root) || {}).status || null,
    flow: 'y',
    gap: 'Z',
    minWidth: '0',
    boxSizing: 'border-box',
    padding: 'A',
    round: 'radiusCard',
    theme: 'layer',
    color: 'title',
    hideDeps: [
      (el, s) => s.root && s.root._workspaceSetup,
      (el, s) => s.root && s.root._ax_activeSessionId
    ],
    hide: (el, s) => !(el.call('workspaceSetupView', s.root) || {}).show,

    SetupTitle: {
      tag: 'h3',
      margin: '0',
      fontSize: 'Z2',
      fontWeight: '700',
      lineHeight: '1.3',
      color: 'title',
      overflowWrap: 'anywhere',
      text: (el, s) => (el.call('workspaceSetupView', s.root) || {}).title || ''
    },

    // the wizard's own setup lines (components/OnboardingStackStep.js
    // OnboardingSetupLine): the glyph, the name, then what happened to it
    SetupLines: {
      tag: 'ul',
      margin: '0',
      padding: '0',
      listStyle: 'none',
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, var(--spacing-F2)), 1fr))',
      gap: 'Y Z',
      minWidth: '0',
      childExtends: 'OnboardingSetupLine',
      childrenAs: 'state',
      childrenDeps: [
        (el, s) => s.root && s.root._workspaceSetup,
        (el, s) => s.root && s.root._wsAppsCatalog
      ],
      children: (el, s) => (el.call('workspaceSetupView', s.root) || {}).lines || []
    },

    SetupFail: {
      tag: 'p',
      margin: '0',
      fontSize: 'Z',
      lineHeight: '1.5',
      color: 'dangerInk',
      hide: (el, s) => !(el.call('workspaceSetupView', s.root) || {}).failText,
      text: (el, s) => (el.call('workspaceSetupView', s.root) || {}).failText || ''
    },

    AppCapsuleBtnGhost: {
      dataSetupAction: 'retry',
      compact: true,
      alignSelf: 'flex-start',
      hide: (el, s) => !(el.call('workspaceSetupView', s.root) || {}).retryable,
      disabled: (el, s) => !!(el.call('workspaceSetupView', s.root) || {}).retrying,
      text: '{{ marketplace.preset.tryAgain | polyglot }}',
      onClick: (e, el) => {
        e.stopPropagation()
        el.call('workspaceSetupRetry')
      }
    },

    // the website card: its name, and the canvas where Publish is
    SetupSite: {
      flow: 'x',
      align: 'center space-between',
      gap: 'Z',
      minWidth: '0',
      boxSizing: 'border-box',
      padding: 'Z Z Z A',
      round: 'radiusCard',
      theme: 'layer',
      hide: (el, s) => !(el.call('workspaceSetupView', s.root) || {}).siteShow,
      SetupSiteName: {
        flow: 'x',
        align: 'center',
        gap: 'Y',
        minWidth: '0',
        Icon: { ariaHidden: 'true', name: 'globe', boxSize: 'iconSm', flexShrink: '0' },
        SetupSiteTitle: {
          tag: 'span',
          minWidth: '0',
          fontSize: 'Z2',
          fontWeight: '500',
          lineHeight: '1.3',
          overflowWrap: 'anywhere',
          text: (el, s) => (el.call('workspaceSetupView', s.root) || {}).siteTitle || ''
        }
      },
      AppCapsuleBtnToolbar: {
        dataSetupAction: 'site',
        compact: true,
        flexShrink: '0',
        text: '{{ workspace.firstRun.publish.action | polyglot }}',
        onClick: (e, el) => {
          e.stopPropagation()
          el.call('workspaceSetupOpenSite', el)
        }
      }
    },

    SetupCredits: {
      tag: 'p',
      margin: '0',
      fontSize: 'Z',
      lineHeight: '1.5',
      color: 'caption',
      hide: (el, s) => !(el.call('workspaceSetupView', s.root) || {}).creditsShow,
      text: (el, s) => (el.call('workspaceSetupView', s.root) || {}).creditsText || ''
    },

    // the first build: what happens next, and the one press it waits on —
    // Build (credits, no waiver) or the claim / top-up page (no credits; the
    // build then goes once they arrive)
    SetupBuild: {
      flow: 'x',
      align: 'center',
      flexWrap: 'wrap',
      gap: 'Z',
      minWidth: '0',
      hide: (el, s) => !(el.call('workspaceSetupView', s.root) || {}).buildShow,
      SetupBuildText: {
        tag: 'p',
        margin: '0',
        flex: '1 1 auto',
        minWidth: '0',
        fontSize: 'Z2',
        lineHeight: '1.5',
        color: 'title',
        text: (el, s) => (el.call('workspaceSetupView', s.root) || {}).buildText || ''
      },
      AppCapsuleBtnPrimary: {
        dataSetupAction: 'build',
        compact: true,
        hide: (el, s) => !(el.call('workspaceSetupView', s.root) || {}).buildAction,
        text: (el, s) => (el.call('workspaceSetupView', s.root) || {}).buildAction || '',
        onClick: (e, el, s) => {
          e.stopPropagation()
          el.call('workspaceSetupBuildPress', s.root)
        }
      },
      AppCapsuleBtnToolbar: {
        dataSetupAction: 'claim',
        compact: true,
        hide: (el, s) => !(el.call('workspaceSetupView', s.root) || {}).claimShow,
        text: (el, s) => (el.call('workspaceSetupView', s.root) || {}).claimText || '',
        onClick: (e, el, s) => {
          e.stopPropagation()
          el.call('workspaceSetupBuildPress', s.root)
          el.call('openInSlot', '/admin/usage', el)
        }
      }
    },

    // the way back: the site the person came from (a platform host), in a
    // new tab — the workspace stays where it is
    Link: {
      dataSetupAction: 'back',
      alignSelf: 'flex-start',
      flow: 'x',
      align: 'center',
      gap: 'X',
      fontSize: 'Z',
      lineHeight: '1.5',
      color: 'caption',
      textDecoration: 'none',
      target: '_blank',
      rel: 'noopener',
      ':hover': { color: 'title', textDecoration: 'underline' },
      ':active': { color: 'title' },
      hide: (el, s) => !(el.call('workspaceSetupView', s.root) || {}).backShow,
      href: (el, s) => (el.call('workspaceSetupView', s.root) || {}).backHref || null,
      Icon: { ariaHidden: 'true', name: 'arrowUpRight', boxSize: 'iconXs', flexShrink: '0' },
      SetupBackText: {
        tag: 'span',
        text: (el, s) => (el.call('workspaceSetupView', s.root) || {}).backText || ''
      }
    }
  }
}
